import { ReactNode } from 'react';

import {
  FieldWrapper,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Skeleton,
  TruncatedText,
} from '@commenda/kakashi';

import { TaskParty } from '@prisma/client';

import { SignedFileLink } from '../../../components/SignedFileLink';
import { ServiceAuditReportHelper } from '../helpers/ServiceAuditReportHelper';
import { ServiceAuditRunStatusHelper } from '../helpers/ServiceAuditRunStatusHelper';
import { useServiceAuditReportSheet } from '../hooks/useServiceAuditReportSheet';
import { ServiceAuditReportBody } from './ServiceAuditReportBody';

interface IServiceAuditReportSheetProps {
  isOpen: boolean;
  onClose: () => void;
  role: TaskParty;
  workflowExecutionId: string | null | undefined;
  auditorStepId: string | null | undefined;
}

/** Right-side report of the auditor step's newest completed audit, with a picker for earlier audits. */
export const ServiceAuditReportSheet = ({
  isOpen,
  onClose,
  role,
  workflowExecutionId,
  auditorStepId,
}: IServiceAuditReportSheetProps) => {
  const {
    canSeeAuditDetails,
    auditOptions,
    runId,
    selectRun,
    clearSelectedRun,
    report,
    mutateReport,
    isFeedbackOpen,
    isReportPending,
    isReportFailed,
  } = useServiceAuditReportSheet({
    isOpen,
    role,
    workflowExecutionId,
    auditorStepId,
  });

  if (!canSeeAuditDetails) return null;

  const closeReportSheet = () => {
    clearSelectedRun();
    onClose();
  };

  const reportBody: ReactNode = report ? (
    <ServiceAuditReportBody
      report={report}
      onFeedbackSaved={isFeedbackOpen ? mutateReport : null}
    />
  ) : isReportPending ? (
    <div className="flex flex-col gap-xl">
      <Skeleton className="h-24 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
    </div>
  ) : isReportFailed ? (
    <p className="txt-small-regular text-text-error">
      {ServiceAuditRunStatusHelper.REPORT_LOAD_ERROR}
    </p>
  ) : null;

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(isSheetOpen) => !isSheetOpen && closeReportSheet()}
    >
      <SheetContent
        side="right"
        className="w-full max-w-xl overflow-y-auto p-3xl"
      >
        <div className="flex flex-col gap-4xl">
          <div className="flex flex-col gap-md">
            <SheetHeader>
              <SheetTitle>{ServiceAuditReportHelper.SHEET_TITLE}</SheetTitle>
            </SheetHeader>
            {report?.subject && (
              <div className="flex min-w-0 items-center gap-md">
                <SignedFileLink
                  file={{
                    id: report.subject.fileId,
                    name: report.subject.fileName,
                  }}
                  label={
                    <TruncatedText>{report.subject.fileName}</TruncatedText>
                  }
                  className="min-w-0"
                />
              </div>
            )}
            {report && (
              <p className="txt-extra-small-regular text-text-tertiary">
                {ServiceAuditReportHelper.getAuditedLine(report)}
              </p>
            )}
            {auditOptions.length >= 2 && runId && (
              <FieldWrapper>
                <Label>{ServiceAuditReportHelper.AUDIT_PICKER_LABEL}</Label>
                <Select value={runId} onValueChange={selectRun}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {auditOptions.map((option) => (
                      <SelectItem key={option.runId} value={option.runId}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldWrapper>
            )}
          </div>
          {reportBody}
        </div>
      </SheetContent>
    </Sheet>
  );
};
